import React from 'react';
import useProductCatalogProducts from '@hooks/catalog-product/use-product-catalog-products';
import useRecentlyViewedProducts from '@hooks/product/use-recently-viewed-products';
import { CatalogProduct } from '@interfaces/models/catalogProduct';
import { Product } from '@interfaces/models/product';
import useUser from '@hooks/user/use-user';
import useFavoritesHotFiltersProductsQuery from '@hooks/favorites/v2/use-favorites-hot-filters-products-query';

export interface ProductQuery {
  arrangement?: string;
  isPersonalizable?: boolean;
  displayTitle?: string;
  productType: string;
  campaign_id?: number;
  ctaText: string;
  sectionIdx: string;
  url: string;
  isPersonalized: boolean;
  productFeedIndex?: 'ng-products-slave-recent' | 'ng-product-master';
  maxProductCount?: number;
  minProductCount?: number;
  fallback?: string;
  fallbackComponents?: { [uid: string]: React.ComponentType };
  ctaUrl?: string;
}

export interface ProductsData {
  products: Product[] | CatalogProduct[];
  isLoading?: boolean;
  isLocal?: boolean;
  isError?: boolean;
}

interface ProductsProviderProps {
  query?: ProductQuery;
}

export const ProductsContext = React.createContext<ProductsData>({ products: [] });

/**
 * Data source for products from SEAS.
 */
export const SeasProductsProvider: React.FC<React.PropsWithChildren<ProductsProviderProps>> = ({ query, children }) => {
  const cta = { href: query.url, title: query.ctaText };

  const isSliderHorizontal = query.arrangement === 'horizontal_slider';
  query.isPersonalized = query.isPersonalizable;

  const { products, isLoading, isError } = useProductCatalogProducts({
    cta,
    ...query,
    isSliderHorizontal,
  });
  const value = { products, isLoading, isError, isLocal: false };

  return <ProductsContext.Provider value={value}>{children}</ProductsContext.Provider>;
};

/**
 * Data source for local storage products.
 */
export const LocalProductsProvider: React.FC<React.PropsWithChildren<ProductsProviderProps>> = ({
  query,
  children,
}) => {
  const { recentlyViewedProducts: products } = useRecentlyViewedProducts();
  const maxProductsCount = query?.maxProductCount ?? 12;
  const value = {
    isLoading: false,
    isError: false,
    isLocal: true,
    products: products?.slice(0, maxProductsCount),
  };

  return <ProductsContext.Provider value={value}>{children}</ProductsContext.Provider>;
};

export const FavouritesProductsProvider: React.FC<React.PropsWithChildren<ProductsProviderProps>> = ({
  query,
  children,
}) => {
  const url = query?.url || '';
  const sortQuery = new URLSearchParams(url.split('?')[1]);
  const { userId, isAuthenticated } = useUser();

  const {
    data: favoritesProducts,
    isLoadingProducts,
    isError,
  } = useFavoritesHotFiltersProductsQuery({
    isAuthenticated,
    userId: userId!,
    sortBy: sortQuery.get('sortBy') || 'dateDesc',
    paginationQuery: { limit: query?.maxProductCount ?? 12, offset: 0 },
  });

  const mappedProducts =
    favoritesProducts?.map((favProduct) => ({
      id: favProduct.productId || favProduct.id,
      ...favProduct,
    })) || [];

  const value = {
    isLocal: false,
    isLoading: isLoadingProducts || false,
    isError: isError || false,
    products: mappedProducts ?? [],
  };

  return <ProductsContext.Provider value={value}>{children}</ProductsContext.Provider>;
};
